import { Button } from '@chakra-ui/react';
import { createContext, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components';
import { BG_CANCEL, PRIMARY_COLOR, TEXT_GRAY_700 } from '@/constants';

interface IConfirm {
  title?: string;
  message?: ReactNode;
  onOk?: () => void;
  onClose?: () => void;
  okText?: string;
  cancelText?: string;
  colorTitle?: string;
  hideCancel?: boolean;
  width?: string;
  widthCancel?: string;
}

interface IState extends IConfirm {
  showConfirm: boolean;
}

interface IConfirmContext {
  toggleConfirm: (value: IConfirm) => void;
}

const ConfirmContext = createContext<IConfirmContext>({
  toggleConfirm: () => {},
});

const initState: IState = {
  showConfirm: false,
  title: undefined,
  message: undefined,
  onOk: undefined,
  onClose: undefined,
  okText: undefined,
  cancelText: undefined,
  colorTitle: undefined,
  hideCancel: false,
  width: undefined,
  widthCancel: undefined,
};

function Component({ children }: { children: ReactNode }) {
  const [state, setState] = useState<IState>(initState);

  const contextValue = useMemo(
    () => ({
      toggleConfirm: (options: IConfirm) => setState({ showConfirm: true, ...options }),
    }),
    []
  );

  const reset = useCallback(() => setState(initState), []);

  const handleOk = useCallback(() => {
    reset();
    state.onOk?.();
  }, [reset, state.onOk]);

  const handleCancel = useCallback(() => {
    reset();
    state.onClose?.();
  }, [reset, state.onClose]);

  const {
    showConfirm,
    title,
    message,
    okText,
    cancelText,
    hideCancel,
    colorTitle,
    width = '448px',
    widthCancel = '128px',
  } = state;

  return (
    <ConfirmContext.Provider value={contextValue}>
      <DialogRoot open={showConfirm} motionPreset="slide-in-right">
        {/* @ts-ignore */}
        <DialogContent style={{ width, maxWidth: width, minHeight: '172px' }}>
          <DialogHeader style={{ padding: '16px 24px' }}>
            {/* @ts-ignore */}
            <DialogTitle
              color={colorTitle ?? TEXT_GRAY_700}
              fontSize="18px"
              fontWeight="700"
              style={{ lineHeight: '150%' }}
            >
              {title}
            </DialogTitle>
          </DialogHeader>

          <DialogBody
            whiteSpace={'pre-line'}
            fontSize={'16px'}
            fontWeight={'400'}
            lineHeight={'150%'}
            color={'#2D3748'}
          >
            {message}
          </DialogBody>

          <DialogFooter
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '1px' }}
          >
            {!hideCancel && (
              <Button
                h="40px"
                onClick={handleCancel}
                variant="subtle"
                bg={BG_CANCEL}
                fontSize="16px"
                lineHeight="24px"
                fontWeight="600"
                color="#1A202C"
                style={{ width: widthCancel }}
                borderRadius={'12px'}
              >
                {cancelText ?? ''}
              </Button>
            )}
            <Button
              h="40px"
              onClick={handleOk}
              width={112}
              colorPalette="teal"
              bg={PRIMARY_COLOR}
              color="white"
              _hover={{ bg: '#38B2AC' }}
              fontSize="16px"
              lineHeight="24px"
              fontWeight="600"
              borderRadius={'12px'}
            >
              {okText ?? ''}
            </Button>
          </DialogFooter>
          {/* @ts-ignore */}
          <DialogCloseTrigger onClick={handleCancel} />
        </DialogContent>
      </DialogRoot>
      {children}
    </ConfirmContext.Provider>
  );
}

const ConfirmProvider = memo(Component);
export { ConfirmContext, ConfirmProvider };
